/**
 * Dispatch route helpers shared by the Customer, Property and Job pages
 * (tracker item 57). Pure functions only; components live in
 * components/RouteControls.tsx.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';

/** Select value for an explicit "no route" (sent as null). */
export const UNROUTED_VALUE = '__unrouted__';

export const ROUTE_CONFLICT_CODE = 'ROUTE_CONFLICT';

export const ROUTE_CONFLICT_MESSAGE =
  'These work items belong to different routes, so the job route cannot be chosen automatically. ' +
  'Set the job route under Schedule and details, save it, then try this work item again.';

/** The route's name; `noneLabel` for none; a placeholder when the list does not have it (deleted, or not loaded). */
export function routeName(routes: types.DispatchRoute[] | null | undefined, routeId: string | null | undefined, noneLabel = 'Unrouted'): string {
  if (!routeId) return noneLabel;
  const route = routes?.find((candidate) => candidate.id === routeId);
  if (!route) return routes ? 'Unknown route' : 'Loading...';
  return route.isActive === false ? `${route.name} (inactive)` : route.name;
}

/** Active routes, plus the current one when it is inactive (kept selectable so a select never blanks it). */
export function routeChoices(routes: types.DispatchRoute[], currentId: string | null | undefined): types.DispatchRoute[] {
  const active = routes.filter((route) => route.isActive !== false);
  if (!currentId || active.some((route) => route.id === currentId)) return active;
  const current = routes.find((route) => route.id === currentId);
  return [...active, current ? { ...current, name: `${current.name} (inactive)` } : { id: currentId, name: 'Current route', isActive: false }];
}

export const ROUTE_SOURCE_LABELS: Record<types.JobRouteSource, string> = {
  DEFAULT: 'From property / customer default',
  ITEM: 'From work items',
  MANUAL: 'Set manually',
};

export function routeSourceLabel(source: types.JobRouteSource | undefined): string {
  return ROUTE_SOURCE_LABELS[source ?? 'DEFAULT'];
}

export function isRouteConflict(err: unknown): boolean {
  return getErrorCode(err) === ROUTE_CONFLICT_CODE;
}

/** Message for a failed job line add / edit / removal; 409 ROUTE_CONFLICT tells the user what to do. */
export function jobLineErrorMessage(err: unknown, fallback: string): string {
  return isRouteConflict(err) ? ROUTE_CONFLICT_MESSAGE : getErrorMessage(err, fallback);
}

export interface PropagationChoice {
  value: types.RoutePropagation;
  label: string;
  description: string;
}

/**
 * The three choices for a changed Customer / Property default, NONE first
 * (the default). Wording follows the backend: ALL also replaces manually set
 * Job routes; a Customer change reaches Jobs at all of its properties.
 */
export function propagationChoices(entity: 'customer' | 'property', oldName: string | null): PropagationChoice[] {
  const scope = entity === 'customer' ? 'this customer’s open jobs (at every property)' : 'open jobs at this property';
  return [
    { value: 'NONE', label: 'Leave existing jobs alone', description: 'Only new jobs use the new default.' },
    { value: 'ALL', label: 'Change all open jobs', description: `Every one of ${scope} gets the new route, including routes set by hand.` },
    oldName
      ? { value: 'MATCH_OLD', label: `Change only jobs on ${oldName}`, description: `Only ${scope} whose route is ${oldName} today.` }
      : { value: 'MATCH_OLD', label: 'Change only unrouted jobs', description: `Only ${scope} that have no route today.` },
  ];
}

/** Success notice after a default route change. */
export function routeDefaultNotice(result: types.SetRouteDefaultResult): string {
  if (result.updatedJobs === 0) return 'Default route saved. No jobs were changed.';
  return `Default route saved. ${result.updatedJobs} job${result.updatedJobs === 1 ? '' : 's'} moved to the new route.`;
}
